import { NavLink, Outlet, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/auth";
import { House, Trophy, Key, SignOut, Target } from "@phosphor-icons/react";
import SEO from "@/components/SEO";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: House, end: true },
  { to: "/admin/results", label: "Manage Results", icon: Trophy },
  { to: "/admin/change-password", label: "Change Password", icon: Key },
];

export default function AdminLayout() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();

  if (loading) return <div className="min-h-screen flex items-center justify-center bg-neutral-950 text-white">Loading…</div>;
  if (!user) return <Navigate to="/admin/login" replace />;

  return (
    <div className="min-h-screen bg-neutral-100 text-neutral-900 flex">
      <SEO title="Admin" description="Admin console" path="/admin" noindex />
      <aside className="w-60 bg-neutral-950 text-white flex-shrink-0 hidden md:flex flex-col" data-testid="admin-sidebar">
        <div className="px-5 py-5 flex items-center gap-2 border-b border-neutral-900">
          <div className="w-9 h-9 rounded-full bg-red-600 flex items-center justify-center">
            <Target size={18} weight="fill" />
          </div>
          <div>
            <div className="font-heading font-black text-sm">JUWAI TEER</div>
            <div className="text-[10px] uppercase tracking-widest text-neutral-500">Admin</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `flex items-center gap-2 px-3 py-2.5 rounded-md text-sm font-semibold ${
                  isActive ? "bg-neutral-800 text-white" : "text-neutral-400 hover:text-white hover:bg-neutral-900"
                }`
              }
              data-testid={`admin-nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
            >
              <n.icon size={16} weight="bold" /> {n.label}
            </NavLink>
          ))}
        </nav>
        <button
          onClick={() => { logout(); nav("/admin/login"); }}
          className="m-3 px-3 py-2.5 rounded-md text-sm font-semibold text-neutral-300 hover:text-white hover:bg-red-600/20 border border-neutral-800 flex items-center gap-2"
          data-testid="admin-logout"
        >
          <SignOut size={16} weight="bold" /> Logout
        </button>
      </aside>

      {/* Mobile top nav */}
      <div className="md:hidden fixed top-0 left-0 right-0 z-40 bg-neutral-950 text-white border-b border-neutral-900">
        <div className="px-4 h-14 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-red-600 flex items-center justify-center"><Target size={16} weight="fill" /></div>
            <div className="font-heading font-black text-sm">JUWAI TEER · Admin</div>
          </div>
          <button onClick={() => { logout(); nav("/admin/login"); }} className="text-xs text-neutral-400" data-testid="admin-logout-mobile"><SignOut size={16} weight="bold" /></button>
        </div>
        <div className="flex overflow-x-auto px-2 pb-2 gap-1">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.end}
              className={({ isActive }) =>
                `whitespace-nowrap px-3 py-1.5 rounded-md text-xs font-semibold ${
                  isActive ? "bg-neutral-800 text-white" : "text-neutral-400"
                }`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </div>
      </div>

      <main className="flex-1 md:p-6 p-4 pt-24 md:pt-6 max-w-full overflow-hidden">
        <Outlet />
      </main>
    </div>
  );
}
